import React, { useEffect } from "react";
import { keyframes } from "@emotion/react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Fab from "@mui/material/Fab";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import SkuflixPlayIcon from "core/icons/skuflix/SkuflixPlayIcon";
import LogoButton from "core/components/LogoButton";
import ShareButton from "core/components/ShareButton";
import { useArena } from "core/components/ArenaProvider";
import { isTelegramMiniApp } from "core/utils/telegram";
import { truncate } from "core/utils/string";

/**
 * Состояние эмбеда до запуска видео.
 *
 * Обложка остаётся видна целиком: вместо затемнения всего кадра только
 * узкие градиенты сверху и снизу, чтобы читались название и логотип.
 * Кнопка Play видна всегда, при наведении слегка увеличивается.
 */
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Root = styled("div")(({ theme }) => ({
  position: "absolute",
  inset: 0,
  zIndex: 1000,
  cursor: "pointer",
  "&::before, &::after": {
    content: "''",
    position: "absolute",
    left: 0,
    right: 0,
    pointerEvents: "none",
  },
  "&::before": {
    top: 0,
    height: "35%",
    background: "linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0))",
  },
  "&::after": {
    bottom: 0,
    height: "25%",
    background: "linear-gradient(to top, rgba(0,0,0,0.45), rgba(0,0,0,0))",
  },
  "&:hover .EmbedPrePlay-play": {
    transform: "translate(-50%, -50%) scale(1.08)",
  },
  // Нажали раньше, чем запустился JS плеера: крутим кольцо вокруг кнопки,
  // видео стартует само после гидратации (см. server/utils/embedEarlyTapScript)
  "&[data-queued] .EmbedPrePlay-play::after": {
    content: "''",
    position: "absolute",
    inset: -6,
    borderRadius: "50%",
    border: "3px solid rgba(255,255,255,0.9)",
    borderTopColor: "transparent",
    animation: `${spin} 0.8s linear infinite`,
    pointerEvents: "none",
  },
  [theme.breakpoints.down("sm")]: {
    "&::before": { height: "45%" },
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  position: "absolute",
  zIndex: 1,
  top: theme.spacing(1.5),
  left: theme.spacing(1.5),
  right: theme.spacing(1.5),
  color: "#fff",
  fontWeight: 600,
  lineHeight: 1.25,
  fontSize: "0.85rem",
  textDecoration: "none",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textShadow: "0 1px 2px rgba(0,0,0,0.5)",
  [theme.breakpoints.up("sm")]: {
    top: theme.spacing(2),
    left: theme.spacing(2.5),
    fontSize: "1.15rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.4rem",
  },
}));

const PlayHolder = styled("div")({
  position: "absolute",
  zIndex: 1,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  transition: "transform .15s ease",
});

/**
 * Размер кнопки Play. В эмбеде уже 600px держим 72px: при большем
 * размере в 320x180 кнопка наезжает на двухстрочное название.
 */
const PLAY_SIZE = { xs: 72, sm: 84 };

/**
 * Картинка кнопки из настроек арены обычно с прозрачными полями,
 * поэтому она крупнее стандартной кнопки: 96 и 120px.
 */
const PLAY_IMAGE_SIZE = { xs: 96, sm: 120 };

const PlayImage = styled("img")(({ theme }) => ({
  display: "block",
  width: PLAY_IMAGE_SIZE.xs,
  height: PLAY_IMAGE_SIZE.xs,
  objectFit: "contain",
  [theme.breakpoints.up("sm")]: {
    width: PLAY_IMAGE_SIZE.sm,
    height: PLAY_IMAGE_SIZE.sm,
  },
}));

const Corner = styled("div")({
  position: "absolute",
  zIndex: 1,
});

/** Клик по логотипу или «Поделиться» не должен запускать видео. */
const stop = (event) => event.stopPropagation();

export default function EmbedPrePlay({
  content,
  onClickPlay,
  hideShare,
  hideLogo,
  disableTitleLink,
}) {
  const { t } = useTranslation("core");
  const arena = useArena();
  const buttonUrl = arena?.embedPlayButton?.url;

  const showLogo = !hideLogo && !content.hideLogo;
  const showShare = !hideShare && !content.hideShare && !isTelegramMiniApp;
  const titleIsLink = !disableTitleLink && showLogo;

  // Нажатие, пойманное до гидратации встроенным в HTML скриптом:
  // запускаем видео сразу, второй раз жать не нужно
  useEffect(() => {
    window.__embedHydrated = true;

    if (window.__embedPlayQueued) {
      window.__embedPlayQueued = false;
      onClickPlay();
    }
  }, []);

  // Клик по самой кнопке не должен всплыть до корня: иначе onClickPlay
  // сработает дважды и плеер дважды попробует создать просмотр.
  const onClickButton = (event) => {
    event.stopPropagation();
    onClickPlay(event);
  };

  return (
    <Root onClick={onClickPlay} data-name="EmbedPrePlay">
      <Title
        component={titleIsLink ? "a" : "div"}
        href={titleIsLink ? content.shareUrl : undefined}
        target={titleIsLink ? "_blank" : undefined}
        rel={titleIsLink ? "noopener" : undefined}
        onClick={titleIsLink ? stop : undefined}
      >
        {truncate(content.title, 120)}
      </Title>

      <PlayHolder className="EmbedPrePlay-play">
        {buttonUrl ? (
          <ButtonBase
            aria-label={t("embedPrePlay.play")}
            onClick={onClickButton}
          >
            <PlayImage src={buttonUrl} alt="" />
          </ButtonBase>
        ) : (
          <Fab
            color="primary"
            size="large"
            sx={{
              width: PLAY_SIZE,
              height: PLAY_SIZE,
              "& svg": { fontSize: { xs: 36, sm: 42 } },
            }}
            aria-label={t("embedPrePlay.play")}
            onClick={onClickButton}
          >
            <SkuflixPlayIcon fontSize="large" />
          </Fab>
        )}
      </PlayHolder>

      {(showShare || showLogo) && (
        <Corner
          sx={{
            bottom: { xs: 2, sm: 6 },
            right: { xs: 2, sm: 6 },
            display: "flex",
            alignItems: "center",
          }}
          onClick={stop}
          data-embed-corner
        >
          {showShare && (
            <ShareButton
              contentId={content.id}
              shareUrl={content.shareUrl}
              shareText={content.title}
              shareDescription={content.rightholder?.name || ""}
              shareImage={content.preview}
            />
          )}
          {showLogo && <LogoButton url={content.shareUrl} arena={arena} />}
        </Corner>
      )}
    </Root>
  );
}

EmbedPrePlay.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    shareUrl: PropTypes.string.isRequired,
    preview: PropTypes.string,
    hideLogo: PropTypes.bool,
    hideShare: PropTypes.bool,
    rightholder: PropTypes.shape({ name: PropTypes.string }),
  }).isRequired,
  onClickPlay: PropTypes.func.isRequired,
  hideShare: PropTypes.bool,
  hideLogo: PropTypes.bool,
  disableTitleLink: PropTypes.bool,
};
